
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface PhysicalAssessmentFormProps {
  visitId?: string;
  patientId?: string;
  onSave?: () => void;
}

const PhysicalAssessmentForm = ({ visitId, patientId, onSave }: PhysicalAssessmentFormProps) => {
  const { profile } = useAuth();
  const [saving, setSaving] = useState(false);
  
  const [vitals, setVitals] = useState({
    bp_systolic: '',
    bp_diastolic: '',
    pulse: '',
    temp: '',
    resp_rate: '',
    o2_sat: '',
    pain: '0'
  });

  const [systems, setSystems] = useState({
    neurological: { findings: '', abnormal: false },
    cardiovascular: { findings: '', abnormal: false },
    respiratory: { findings: '', abnormal: false },
    gastrointestinal: { findings: '', abnormal: false },
    genitourinary: { findings: '', abnormal: false },
    musculoskeletal: { findings: '', abnormal: false },
    skin: { findings: '', abnormal: false }
  });

  const handleSaveAssessment = async () => {
    if (!visitId || !patientId || !profile) {
      toast.error('Missing required information');
      return;
    }

    setSaving(true);

    try {
      const assessmentData = {
        visit_id: visitId,
        patient_id: patientId,
        nurse_id: profile.id,
        
        // Vital Signs
        blood_pressure_systolic: vitals.bp_systolic ? parseInt(vitals.bp_systolic) : null,
        blood_pressure_diastolic: vitals.bp_diastolic ? parseInt(vitals.bp_diastolic) : null,
        pulse: vitals.pulse ? parseInt(vitals.pulse) : null,
        temperature: vitals.temp ? parseFloat(vitals.temp) : null,
        respiratory_rate: vitals.resp_rate ? parseInt(vitals.resp_rate) : null,
        oxygen_saturation: vitals.o2_sat ? parseInt(vitals.o2_sat) : null,
        pain_level: parseInt(vitals.pain),
        
        // System Assessments
        neurological: systems.neurological.findings,
        neurological_abnormal: systems.neurological.abnormal,
        cardiovascular: systems.cardiovascular.findings,
        cardiovascular_abnormal: systems.cardiovascular.abnormal,
        respiratory: systems.respiratory.findings,
        respiratory_abnormal: systems.respiratory.abnormal,
        gastrointestinal: systems.gastrointestinal.findings,
        gastrointestinal_abnormal: systems.gastrointestinal.abnormal,
        genitourinary: systems.genitourinary.findings,
        genitourinary_abnormal: systems.genitourinary.abnormal,
        musculoskeletal: systems.musculoskeletal.findings,
        musculoskeletal_abnormal: systems.musculoskeletal.abnormal,
        skin: systems.skin.findings,
        skin_abnormal: systems.skin.abnormal
      };

      const { error } = await supabase
        .from('physical_assessments')
        .insert(assessmentData);

      if (error) throw error;

      toast.success('Assessment saved successfully!');
      
      // Clear form
      setVitals({
        bp_systolic: '',
        bp_diastolic: '',
        pulse: '',
        temp: '',
        resp_rate: '',
        o2_sat: '',
        pain: '0'
      });
      
      setSystems({
        neurological: { findings: '', abnormal: false },
        cardiovascular: { findings: '', abnormal: false },
        respiratory: { findings: '', abnormal: false },
        gastrointestinal: { findings: '', abnormal: false },
        genitourinary: { findings: '', abnormal: false },
        musculoskeletal: { findings: '', abnormal: false },
        skin: { findings: '', abnormal: false }
      });

      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error('Error saving assessment:', error);
      toast.error('Failed to save assessment. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Physical Assessment</h2>
      
      {/* Vital Signs */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Vital Signs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Blood Pressure</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  placeholder="Systolic"
                  value={vitals.bp_systolic}
                  onChange={(e) => setVitals({...vitals, bp_systolic: e.target.value})}
                  className="w-24 px-3 py-2 border rounded-md"
                />
                <span className="py-2">/</span>
                <input
                  type="number"
                  placeholder="Diastolic"
                  value={vitals.bp_diastolic}
                  onChange={(e) => setVitals({...vitals, bp_diastolic: e.target.value})}
                  className="w-24 px-3 py-2 border rounded-md"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Pulse (BPM)</label>
              <input
                type="number"
                value={vitals.pulse}
                onChange={(e) => setVitals({...vitals, pulse: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Temperature (°F)</label>
              <input
                type="number"
                step="0.1"
                value={vitals.temp}
                onChange={(e) => setVitals({...vitals, temp: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">O2 Saturation (%)</label>
              <input
                type="number"
                value={vitals.o2_sat}
                onChange={(e) => setVitals({...vitals, o2_sat: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Respiratory Rate</label>
              <input
                type="number"
                value={vitals.resp_rate}
                onChange={(e) => setVitals({...vitals, resp_rate: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Pain Level (0-10)</label>
              <select
                value={vitals.pain}
                onChange={(e) => setVitals({...vitals, pain: e.target.value})}
                className="w-full px-3 py-2 border rounded-md"
              >
                {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Assessments */}
      <Card>
        <CardHeader>
          <CardTitle>System Assessment</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(systems).map(([system, data]) => (
              <div key={system} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium capitalize">{system}</h4>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={data.abnormal}
                      onChange={(e) => setSystems({
                        ...systems,
                        [system]: { ...data, abnormal: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="text-sm text-red-600">Abnormal findings</span>
                  </label>
                </div>
                <textarea
                  value={data.findings}
                  onChange={(e) => setSystems({
                    ...systems,
                    [system]: { ...data, findings: e.target.value }
                  })}
                  placeholder="Enter findings..."
                  className="w-full px-3 py-2 border rounded-md"
                  rows={2}
                />
              </div>
            ))}
          </div>
          
          <div className="mt-6">
            <Button 
              onClick={handleSaveAssessment}
              disabled={saving || !visitId || !patientId}
              className="w-full"
            >
              {saving ? 'Saving...' : 'Save Assessment'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PhysicalAssessmentForm;
